import { auth } from './firebase';

// Client for /api/generate-isometric: a single sharp call over the plan
// sketch (Nano Banana Pro first, Flash as fallback). Empirically the simple
// single-image prompt is the most layout-faithful — no brief, no style ref.

// In production the edge function is served from the same origin. `vite dev`
// has no edge runtime, so point VITE_API_BASE at the deployed site to develop
// against it.
const API_BASE = import.meta.env.DEV ? import.meta.env.VITE_API_BASE || '' : '';

// Optional camera-angle + consistency reference: a known-good dollhouse render
// of this building, sent only for FLOOR generations so portrait plans can't
// collapse to a flat top-down view and stairs/terraces/materials stay
// consistent across floors. Apartment sketches are sent alone — the short
// single-image call is the most layout-faithful for them.
//
// Per project: once one floor renders well, upload that image and put its URL
// in VITE_ISO_ANGLE_REF_URL. Without it generation still works, just without
// the camera anchor.
const ANGLE_REF_URL = import.meta.env.VITE_ISO_ANGLE_REF_URL || '';

export interface IsometricResult {
  blob: Blob;
  model: 'pro' | 'flash';
}

export type RenderKind = 'floor' | 'apartment';

const callGenerate = async (
  imageUrl: string,
  kind: RenderKind,
  quality: 'pro' | 'flash'
): Promise<IsometricResult> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Не сте влезли в системата');

  const res = await fetch(`${API_BASE}/api/generate-isometric`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      imageUrl,
      kind,
      quality,
      ...(kind === 'floor' && ANGLE_REF_URL ? { angleRefUrl: ANGLE_REF_URL } : {}),
    }),
  });

  const data = await res.json().catch(() => ({} as any));
  if (!res.ok || data.error) {
    throw new Error(data.error || `Генерирането се провали (${res.status})`);
  }

  const bin = atob(data.imageBase64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return {
    blob: new Blob([bytes], { type: data.mimeType || 'image/png' }),
    model: data.model === 'flash' ? 'flash' : 'pro',
  };
};

// Pro first; if it fails (e.g. the edge time limit), retry with Flash. The
// caller shows which model produced the result so a Flash fallback is visible.
export const runIsometricPipeline = async (
  imageUrl: string,
  kind: RenderKind,
  onStage?: (stage: string) => void
): Promise<IsometricResult> => {
  try {
    onStage?.('Генерирам (Pro)...');
    return await callGenerate(imageUrl, kind, 'pro');
  } catch (err) {
    console.warn('Pro render failed, retrying with Flash:', err);
    onStage?.('Генерирам (Flash)...');
    return await callGenerate(imageUrl, kind, 'flash');
  }
};
